import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Check, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CookieConsent from '../components/CookieConsent';
import { API_URL } from '../config/api';
import './LandingPage.css';

// Регистрируем плагин ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [testimonialsRef, setTestimonialsRef] = useState<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const [subscriptionDiscounts, setSubscriptionDiscounts] = useState<any[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<number[]>([1]); // Доступные периоды из API
  const [tariffs, setTariffs] = useState<any[]>([]); // Тарифы из API

  // Refs для анимаций
  const heroRef = useRef<HTMLElement>(null);
  const stepsRef = useRef<HTMLElement>(null);
  const audienceRef = useRef<HTMLElement>(null);
  const casesRef = useRef<HTMLElement>(null);
  const testimonialsContainerRef = useRef<HTMLElement>(null);
  const pricingRef = useRef<HTMLElement>(null);
  const faqRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);

  // Принудительная прокрутка в начало при загрузке - ВАЖНО: должно быть первым
  useLayoutEffect(() => {
    // Сразу прокручиваем в начало
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index: number) => {
    const newOpenFaq = openFaq === index ? null : index;
    
    if (openFaq !== null && openFaq !== index) {
      // Закрываем предыдущий FAQ с анимацией
      const previousAnswer = document.querySelector(`.faq-item:nth-child(${openFaq + 1}) .faq-answer`);
      if (previousAnswer) {
        gsap.to(previousAnswer, {
          duration: 0.4,
          height: 0,
          opacity: 0,
          ease: 'power3.inOut',
          onComplete: () => {
            setOpenFaq(newOpenFaq);
            if (newOpenFaq !== null) {
              // Открываем новый FAQ с анимацией
              requestAnimationFrame(() => {
                const newAnswer = document.querySelector(`.faq-item:nth-child(${newOpenFaq + 1}) .faq-answer`);
                if (newAnswer) {
                  gsap.fromTo(newAnswer, 
                    { height: 0, opacity: 0 },
                    { duration: 0.5, height: 'auto', opacity: 1, ease: 'power3.out' }
                  );
                }
              });
            }
          }
        });
      }
    } else {
      setOpenFaq(newOpenFaq);
      
      // Анимация для открытия/закрытия FAQ
      if (newOpenFaq !== null) {
        requestAnimationFrame(() => {
          const answer = document.querySelector(`.faq-item:nth-child(${newOpenFaq + 1}) .faq-answer`);
          if (answer) {
            gsap.fromTo(answer, 
              { height: 0, opacity: 0 },
              { duration: 0.5, height: 'auto', opacity: 1, ease: 'power3.out' }
            );
          }
        });
      } else if (openFaq !== null) {
        // Закрываем текущий FAQ
        const answer = document.querySelector(`.faq-item:nth-child(${openFaq + 1}) .faq-answer`);
        if (answer) {
          gsap.to(answer, {
            duration: 0.4,
            height: 0,
            opacity: 0,
            ease: 'power3.inOut'
          });
        }
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const container = e.currentTarget as HTMLElement;
    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const container = e.currentTarget as HTMLElement;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 2;
    container.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const container = e.currentTarget as HTMLElement;
    container.scrollLeft += e.deltaY;
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/knowledge');
    } else {
      navigate('/auth/login');
    }
  };

  const handlePlanSelect = (plan: string) => {
    if (isAuthenticated) {
      navigate('/knowledge');
    } else {
      navigate(`/auth/register?plan=${plan}&period=${selectedPeriod}`);
    }
  };

  const handleSignIn = () => {
    navigate('/auth/login');
  };

  const handleRegister = () => {
    navigate('/auth/register');
  };

  // Вспомогательная функция для склонения слов
  const getPlural = (count: number, one: string, two: string, five: string) => {
    if (count === -1 || count === 0) return five; // Для безлимитных
    const n = Math.abs(count);
    if (n % 100 >= 5 && n % 100 <= 20) {
      return five;
    }
    if (n % 10 === 1) {
      return one;
    }
    if (n % 10 >= 2 && n % 10 <= 4) {
      return two;
    }
    return five;
  };

  // Получить тариф по типу и имени
  const getTariffByTypeAndName = (type: string, name: string) => {
    return tariffs.find(t => t.type === type && t.name === name) || null;
  };

  // Получить тариф по типу (для Individual тарифов берем первый попавшийся)
  const getTariffByType = (type: string) => {
    return tariffs.find(t => t.type === type) || null;
  };

  // Получить базовую цену тарифа в рублях
  const getTariffBasePrice = (tariffType: string, tariffName?: string) => {
    let tariff;
    if (tariffName) {
      tariff = getTariffByTypeAndName(tariffType, tariffName);
    } else {
      tariff = getTariffByType(tariffType);
    }
    return tariff ? Math.round(tariff.basePrice) : 0; // Предполагаем что цена в долларах, конвертируем в рубли
  };

  // Получить скидку по выбранному периоду
  const getDiscountForPeriod = (months: number) => {
    return subscriptionDiscounts.find(d => d.months === months && d.isActive) || null;
  };

  // Получить цену за месяц с учетом скидки
  const getMonthlyPrice = (tariffType: string, months: number, tariffName?: string) => {
    const basePrice = getTariffBasePrice(tariffType, tariffName);
    // Применяем скидку к месячной цене
    const periodDiscount = getDiscountForPeriod(months);
    if (periodDiscount && periodDiscount.discountPercent > 0) {
      return Math.round(basePrice * (1 - periodDiscount.discountPercent / 100));
    }
    return basePrice;
  };

  // Рассчитать итоговую цену за период
  const calculatePriceWithDiscount = (tariffType: string, months: number, tariffName?: string) => {
    const monthlyPrice = getMonthlyPrice(tariffType, months, tariffName);
    return monthlyPrice * months;
  };

  // Центрируем карусель при загрузке
  React.useEffect(() => {
    if (testimonialsRef) {
      const containerWidth = testimonialsRef.offsetWidth;
      const scrollWidth = testimonialsRef.scrollWidth;
      const centerPosition = (scrollWidth - containerWidth) / 2;
      testimonialsRef.scrollLeft = centerPosition;
    }
  }, [testimonialsRef]);

  // Загружаем скидки по периодам и тарифы
  React.useEffect(() => {
    const loadData = async () => {
      try {
        // Загружаем скидки
        const discountsResponse = await fetch(`${API_URL}/subscriptiondiscount/all`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (discountsResponse.ok) {
          const discounts = await discountsResponse.json();
          setSubscriptionDiscounts(discounts);

          // Извлекаем доступные периоды из скидок (всегда включаем 1 месяц)
          const periods = [1]; // Всегда показываем 1 месяц
          discounts.forEach((discount: any) => {
            if (discount.months > 1 && !periods.includes(discount.months)) {
              periods.push(discount.months);
            }
          });
          setAvailablePeriods(periods.sort((a, b) => a - b));

          // Устанавливаем первый доступный период как выбранный по умолчанию
          if (periods.length > 0 && !periods.includes(selectedPeriod)) {
            setSelectedPeriod(periods[0]);
          }
        }

        // Загружаем тарифы
        const tariffsResponse = await fetch(`${API_URL}/subscriptionpayment/tariffs`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (tariffsResponse.ok) {
          const tariffsData = await tariffsResponse.json();
          setTariffs(tariffsData);
        }
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
        // Продолжаем без скидок и тарифов
      }
    };

    loadData();
  }, []);

  // Отслеживаем скролл для изменения стилей шапки
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // GSAP анимации
  useEffect(() => {
    // Проверяем, что GSAP загрузился корректно
    if (!gsap || !ScrollTrigger) {
      console.warn('GSAP или ScrollTrigger не загружены');
      return;
    }

    // Сброс анимаций при первой загрузке - применяем только к элементам hero секции
    gsap.set([
      '.hero-title', '.hero-subtitle', '.btn-hero-primary', '.hero-note', '.hero-badges'
    ], {
      opacity: 0,
      y: 50
    });

    // Убеждаемся, что все остальные элементы видны по умолчанию
    gsap.set([
      '.problem-item', '.step', '.comparison-row', '.audience-card', '.feature-block',
      '.testimonial', '.pricing-card', '.referral-card', '.faq-item'
    ], {
      opacity: 1,
      y: 0
    });

    // Анимация героя
    const heroTimeline = gsap.timeline();
    heroTimeline
      .to('.hero-title', { duration: 1, opacity: 1, y: 0, ease: 'power3.out' })
      .to('.hero-subtitle', { duration: 0.8, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.6')
      .to('.btn-hero-primary', { duration: 0.6, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.4')
      .to('.hero-note', { duration: 0.5, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.3')
      .to('.hero-badges', { duration: 0.6, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.2');

    // Анимация графики героя
    gsap.set('.dashboard-graphic', { opacity: 0, x: 100, rotation: 5 });
    gsap.to('.dashboard-graphic', { 
      duration: 1.2, 
      opacity: 1, 
      x: 0, 
      rotation: 0, 
      ease: 'power3.out',
      delay: 0.5
    });

    // Анимация проблем при скролле
    try {
      const problemItems = gsap.utils.toArray('.problem-item');
      if (problemItems.length > 0) {
        problemItems.forEach((item, index) => {
          if (item && item instanceof HTMLElement) {
            gsap.fromTo(item,
              { opacity: 0, y: 50 },
              {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power3.out',
                scrollTrigger: {
                  trigger: item,
                  start: 'top 90%',
                  once: true
                },
                delay: index * 0.1
              }
            );
          }
        });
      }
    } catch (error) {
      console.warn('Ошибка анимации проблем:', error);
    }

    // Анимация шагов
    try {
      const steps = gsap.utils.toArray('.step');
      if (steps.length > 0) {
        steps.forEach((step, index) => {
          if (step && step instanceof HTMLElement) {
            gsap.fromTo(step,
              { opacity: 0, y: 50 },
              {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power3.out',
                scrollTrigger: {
                  trigger: step,
                  start: 'top 85%',
                  once: true
                },
                delay: index * 0.2
              }
            );
          }
        });
      }
    } catch (error) {
      console.warn('Ошибка анимации шагов:', error);
    }

    // Анимация таблицы сравнения
    try {
      const comparisonRows = gsap.utils.toArray('.comparison-row');
      if (comparisonRows.length > 0) {
        comparisonRows.forEach((row, index) => {
          if (row && row instanceof HTMLElement) {
            gsap.fromTo(row,
              { opacity: 0, y: 50 },
              {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'power3.out',
                scrollTrigger: {
                  trigger: row,
                  start: 'top 95%',
                  once: true
                },
                delay: index * 0.1
              }
            );
          }
        });
      }
    } catch (error) {
      console.warn('Ошибка анимации таблицы сравнения:', error);
    }

    // Анимация карточек аудитории
    try {
      const audienceCards = gsap.utils.toArray('.audience-card');
      if (audienceCards.length > 0) {
        audienceCards.forEach((card, index) => {
          if (card && card instanceof HTMLElement) {
            const icon = card.querySelector('.audience-icon');

            const cardTimeline = gsap.timeline({
              scrollTrigger: {
                trigger: card,
                start: 'top 90%',
                once: true
              }
            });

            cardTimeline
              .fromTo(card,
                { opacity: 0, y: 50 },
                {
                  duration: 0.8,
                  opacity: 1,
                  y: 0,
                  ease: 'power3.out',
                  delay: index * 0.2
                }
              )
              .fromTo(icon,
                { scale: 0, rotation: 180 },
                {
                  duration: 0.6,
                  scale: 1,
                  rotation: 0,
                  ease: 'back.out(1.7)'
                }, '-=0.4');
          }
        });
      }
    } catch (error) {
      console.warn('Ошибка анимации карточек аудитории:', error);
    }

    // Анимация блоков кейсов
    try {
      const featureBlocks = gsap.utils.toArray('.feature-block');
      if (featureBlocks.length > 0) {
        featureBlocks.forEach((block, index) => {
          if (block && block instanceof HTMLElement) {
            const content = block.querySelector('.feature-content');
            const image = block.querySelector('.feature-image');

            const blockTimeline = gsap.timeline({
              scrollTrigger: {
                trigger: block,
                start: 'top 85%',
                once: true
              }
            });

            blockTimeline
              .fromTo(content,
                { opacity: 0, x: index % 2 === 0 ? -50 : 50 },
                {
                  duration: 0.8,
                  opacity: 1,
                  x: 0,
                  ease: 'power3.out'
                }
              )
              .fromTo(image,
                { opacity: 0, x: index % 2 === 0 ? 50 : -50 },
                {
                  duration: 0.8,
                  opacity: 1,
                  x: 0,
                  ease: 'power3.out'
                }, '-=0.4');
          }
        });
      }
    } catch (error) {
      console.warn('Ошибка анимации блоков кейсов:', error);
    }

    // Анимация отзывов
    try {
      const testimonials = gsap.utils.toArray('.testimonial');
      if (testimonials.length > 0) {
        testimonials.forEach((testimonial, index) => {
          if (testimonial && testimonial instanceof HTMLElement) {
            gsap.fromTo(testimonial,
              { opacity: 0, y: 50 },
              {
                opacity: 1,
                y: 0,
                duration: 0.6,
                immediateRender: false,
                ease: 'power3.out',
                scrollTrigger: {
                  trigger: '.testimonials',
                  start: 'top 90%',
                  once: true
                },
                delay: index * 0.1
              }
            );
          }
        });
      }
    } catch (error) {
      console.warn('Ошибка анимации отзывов:', error);
    }

    // Анимация карточек цен
    try {
      const pricingCards = gsap.utils.toArray('.pricing-card');
      if (pricingCards.length > 0) {
        pricingCards.forEach((card, index) => {
          if (card && card instanceof HTMLElement) {
            const isPopular = card.classList.contains('popular');

            gsap.fromTo(card,
              { opacity: 0, y: 50 },
              {
                opacity: 1,
                y: 0,
                duration: 0.8,
                immediateRender: false,
                ease: 'power3.out',
                scrollTrigger: {
                  trigger: card,
                  start: 'top 95%',
                  once: true
                },
                delay: isPopular ? 0 : index * 0.1
              }
            );

            if (isPopular) {
              gsap.to(card, {
                scale: 1.05,
                duration: 0.3,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: card,
                  start: 'top 95%',
                  once: true
                },
                delay: 0.5
              });
            }
          }
        });
      }
    } catch (error) {
      console.warn('Ошибка анимации карточек цен:', error);
    }

    // Анимация реферальных карточек
    try {
      const referralCards = gsap.utils.toArray('.referral-card');
      if (referralCards.length > 0) {
        referralCards.forEach((card, index) => {
          if (card && card instanceof HTMLElement) {
            const icon = card.querySelector('.referral-icon');
            const benefits = card.querySelectorAll('.benefit-item');

            const cardTimeline = gsap.timeline({
              scrollTrigger: {
                trigger: card,
                start: 'top 90%',
                once: true
              }
            });

            cardTimeline
              .fromTo(card,
                { opacity: 0, y: 50 },
                {
                  duration: 0.8,
                  opacity: 1,
                  y: 0,
                  ease: 'power3.out',
                  delay: index * 0.2,
                  clearProps: "transform"
                }
              )
              .fromTo(icon,
                { scale: 0, rotation: 180 },
                {
                  duration: 0.6,
                  scale: 1,
                  rotation: 0,
                  ease: 'back.out(1.7)'
                }, '-=0.4')
              .fromTo(benefits,
                { opacity: 0, x: -20 },
                {
                  duration: 0.5,
                  opacity: 1,
                  x: 0,
                  ease: 'power3.out',
                  stagger: 0.1
                }, '-=0.3');
          }
        });
      }
    } catch (error) {
      console.warn('Ошибка анимации реферальных карточек:', error);
    }

    // Анимация FAQ
    const faqItems = gsap.utils.toArray('.faq-item');
    if (faqItems.length > 0) {
      faqItems.forEach((item, index) => {
        if (item && (item as Element).tagName) {
          gsap.fromTo(item as Element,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.6,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: item as Element,
                start: 'top 95%',
                once: true
              },
              delay: index * 0.1
            }
          );
        }
      });
    }

    // Анимация CTA секции
    gsap.fromTo('.cta-title', 
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.cta-section',
          start: 'top 80%',
          once: true
        }
      }
    );

    gsap.fromTo('.cta-subtitle', 
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.cta-section',
          start: 'top 80%',
          once: true
        },
        delay: 0.2
      }
    );

    gsap.fromTo('.btn-cta-primary', 
      { opacity: 0, scale: 0.9 },
      {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: '.cta-section',
          start: 'top 80%',
          once: true
        },
        delay: 0.4
      }
    );

    gsap.fromTo('.cta-note', 
      { opacity: 0, y: 20 },
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.cta-section',
          start: 'top 80%',
          once: true
        },
        delay: 0.6
      }
    );

    // Анимация кнопок при наведении
    const buttons = document.querySelectorAll('.btn-hero-primary, .btn-primary, .btn-secondary, .btn-cta-primary');
    buttons.forEach(button => {
      button.addEventListener('mouseenter', () => {
        gsap.to(button, { 
          duration: 0.3, 
          scale: 1.05, 
          ease: 'power2.out'
        });
      });
      
      button.addEventListener('mouseleave', () => {
        gsap.to(button, { 
          duration: 0.3, 
          scale: 1, 
          ease: 'power2.out'
        });
      });
    });

    // Очистка при размонтировании
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="landing">
      {/* Header */}
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src="/logo.png" alt="Dissai" className="logo-image" />
              <span className="logo-text">Dissai.io</span>
            </div>
            
            <div className="header-actions">
              <button className="btn-sign-in" onClick={handleSignIn}>
                Войти
              </button>
              <button className="btn-contact" onClick={handleRegister}>
                Регистрация
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-stripe" ref={heroRef}>
        <div className="hero-diagonal"></div>
        <div className="container">
          <div className="hero-layout">
            <div className="hero-content">
              <h1 className="hero-title">
              Вирусные идеи для Reels на месяц <br/> за 15 минут
              </h1>
              
              <p className="hero-subtitle">
              Dissai анализирует конкурентов и тренды, а ты получаешь готовый контент-план, который вовлекает и продаёт.
              </p>
              
              <div className="hero-cta">
                <button className="btn-hero-primary" onClick={handleGetStarted}>
                 Начать бесплатно
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
            
            <div className="hero-graphics">
              <div className="dashboard-graphic">
                {/* Пустой блок для скрина */}
              </div>
            </div>
            
            {/* Мобильное демо - показывается только на мобильных */}
            <div className="mobile-phone">
              <div className="mobile-screen">
                {/* Контент мобильного демо */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="target-audience" ref={audienceRef}>
        <div className="container">
          <h2 className="section-title">Кому подходит Dissai?</h2>
          
          <div className="audience-grid">
            <div className="audience-card">
              <div className="audience-icon">
                <img src="/images/onboarding/LandingImages/expert.png" alt="Эксперт" />
              </div>
              <h3>Эксперт</h3>
              <p className="audience-problem">Вы разбираетесь в своём деле, но не знаете, как упаковать знания в контент и привлекать клиентов через соцсети.</p>
              <div className="audience-divider"></div>
              <p className="audience-solution"><strong>С Dissai:</strong> за несколько минут вы получаете контент-план, который показывает вашу экспертность, упрощает создание контента  и помогает превратить подписчиков в клиентов.</p>
            </div>
            
            <div className="audience-card">
              <div className="audience-icon">
                <img src="/images/onboarding/LandingImages/blogger.png" alt="Блогер" />
              </div>
              <h3>Блогер</h3>
              <p className="audience-problem">Вы хотите стабильно расти, но идей не хватает: публикации откладываются, вдохновения нет.</p>
              <div className="audience-divider"></div>
              <p className="audience-solution"><strong>С Dissai:</strong> сервис подбирает залетевшие тренды и предлагает готовые сценарии Reels и постов, чтобы ваш блог не простаивал и рос каждый месяц.</p>
            </div>
            
            <div className="audience-card">
              <div className="audience-icon">
                <img src="/images/onboarding/LandingImages/smm.png" alt="SMM-специалист" />
              </div>
              <h3>SMM-специалист</h3>
              <p className="audience-problem">У вас несколько клиентов из разных ниш, и каждый день нужно придумывать десятки идей.</p>
              <div className="audience-divider"></div>
              <p className="audience-solution"><strong>С Dissai:</strong> вы получаете готовый контент-план и подборку вирусных идей для каждой ниши, экономите до 10 часов в неделю и больше не переживаете за «пустой контент-план».</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="how-it-works" ref={stepsRef}>
        <div className="container">
          <h2 className="section-title">3 шага к готовому контент-плану</h2>
          
          <div className="steps">
            <div className="step">
              <h3>Распаковка с ИИ</h3>
              <div className="step-image-wrapper">
                <img 
                  src="/images/onboarding/LandingImages/1step.png" 
                  alt="Распаковка с ИИ" 
                  className="step-image"
                />
              </div>
              <p>Простой опрос поможет выявить ваши ценности, нишу и стиль.</p>
            </div>
            
            <div className="step">
              <h3>Готовый контент-план</h3>
              <div className="step-image-wrapper">
                <img 
                  src="/images/onboarding/LandingImages/2step.png" 
                  alt="Готовый контент-план" 
                  className="step-image"
                />
              </div>
              <p>Вы сразу получаете посты, Reels и карусели на 2 недели вперёд.</p>
            </div>
            
            <div className="step">
              <h3>Умная персонализация</h3>
              <div className="step-image-wrapper">
                <img 
                  src="/images/onboarding/LandingImages/3step.png" 
                  alt="Умная персонализация" 
                  className="step-image"
                />
              </div>
              <p>Каждое использование делает план ещё точнее и ближе к вашему стилю.</p>
            </div>
          </div>
          
        </div>
      </section>

      <section className="cases" ref={casesRef}>
        <div className="container">
          {/* Анализ целевой аудитории */}
          <div className="feature-block">
            <div className="feature-content">
              <h2 className="section-title">Анализ целевой аудитории</h2>
              <ul className="feature-list">
                <li>Начинает с вашего продукта, чтобы понять аудиторию.</li>
                <li>От демографии и образа жизни до ценностей и болей.</li>
                <li>Что мешает клиентам купить и как это преодолеть.</li>
                <li>Какие эмоции и события ведут к решению о покупке.</li>
                <li>Что аудитория считает успехом и на какие сигналы доверяет.</li>
              </ul>
              <div className="feature-result">
                <span className="feature-result-label">Результат</span>
                <p>Вы знаете, кто именно ваша аудитория и какие темы вызовут у неё отклик.</p>
              </div>
            </div>
            <div className="feature-image">
              <div className="window-wrapper">
                <div className="window-header">
                  <div className="window-controls">
                    <div className="window-control close"></div>
                    <div className="window-control minimize"></div>
                    <div className="window-control maximize"></div>
                  </div>
                  <div className="window-title">Анализ ЦА</div> 
                </div>
                <div className="window-content">
                  <img src="images/onboarding/LandingImages/audience.png" alt="Анализ целевой аудитории" style={{width: '100%', height: 'auto', display: 'block'}} />
                </div>
              </div>
            </div>
          </div>

          {/* Анализ успешных Reels */}
          <div className="feature-block reverse">
            <div className="feature-content">
              <h2 className="section-title">Анализ Reels</h2>
              <ul className="feature-list">
                <li>Анализ по ссылке и аккаунтам</li>
                <li>Получите: ключевую мысль, хук, AIDA анализ, советы по улучшению</li>
                <li>Поймете, какой формат и хук работают</li>
                <li>Массовый анализ конкурентов</li>
              </ul>
              <div className="feature-result">
                <span className="feature-result-label">Результат</span>
                <p>Получите анализ сотен Reels, чтобы понять, что работает в вашей нише</p>
              </div>
            </div>
            <div className="feature-image">
              <div className="window-wrapper">
                <div className="window-header">
                  <div className="window-controls">
                    <div className="window-control close"></div>
                    <div className="window-control minimize"></div>
                    <div className="window-control maximize"></div>
                  </div>
                  <div className="window-title">Анализ Reels</div>
                </div>
                <div className="window-content">
                  <img src="images/onboarding/LandingImages/reels.png" alt="Анализ целевой аудитории" style={{width: '100%', height: 'auto', display: 'block'}} />
                </div>
              </div>
            </div>
          </div>

          {/* Умный контент-план */}
          <div className="feature-block">
            <div className="feature-content">
              <h2 className="section-title">Умный контент-план</h2>
              <ul className="feature-list">
                <li>Генерация персонального плана, которую можно продолжать</li>
                <li>Посты, сценарии Reels и экспертные карусели</li>
                <li>Учёт вашего стиля общения по вашему аккаунту</li>
                <li>Учёт формата референсного аккаунта</li>
                <li>Адаптация «залетевших» Reels</li>
              </ul>
              <div className="feature-result">
                <span className="feature-result-label">Результат</span>
                <p>У вас готов контент-план, который можно публиковать сразу.</p>
              </div>
            </div>
            <div className="feature-image">
              <div className="window-wrapper">
                <div className="window-header">
                  <div className="window-controls">
                    <div className="window-control close"></div>
                    <div className="window-control minimize"></div>
                    <div className="window-control maximize"></div>
                  </div>
                  <div className="window-title">Контент-план</div>
                </div>
                <div className="window-content">
                  <img src="images/onboarding/LandingImages/content_plan.png" alt="Анализ целевой аудитории" style={{width: '100%', height: 'auto', display: 'block'}} />
                </div>
              </div>
            </div>
          </div>

          {/* Персонализация и обучение */}
          <div className="feature-block reverse">
            <div className="feature-content">
              <h2 className="section-title">Персонализация и обучение на ваших данных</h2>
              <ul className="feature-list">
                <li>Dissai запоминает ваш стиль.</li>
                <li>Учитывает статистику ваших публикаций.</li>
                <li>Автоматически подстраивается под лучшие форматы.</li>
                <li>Становится точнее с каждым месяцем.</li>
              </ul>
              <div className="feature-result">
                <span className="feature-result-label">Результат</span>
                <p>Ваш контент становится всё более точным и эффективным, без повторов и шаблонности.</p>
              </div>
            </div>
            <div className="feature-image">
              <div className="window-wrapper">
                <div className="window-header">
                  <div className="window-controls">
                    <div className="window-control close"></div>
                    <div className="window-control minimize"></div>
                    <div className="window-control maximize"></div>
                  </div>
                  <div className="window-title">Персонализация</div>
                </div>
                <div className="window-content">
                  <div className="image-placeholder"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Работа с несколькими аккаунтами */}
          <div className="feature-block reverse">
            <div className="feature-content">
              <h2 className="section-title">Работа по несколькими проектам</h2>
              <ul className="feature-list">
                <li>Управление сразу несколькими клиентами/проектами.</li>
                <li>Собственная структура файлов</li>
                <li>Гибкое переключение между проектами</li>
                <li>Совместное управление с коллегами</li>
              </ul>
              <div className="feature-result">
                <span className="feature-result-label">Результат</span>
                <p>Вы ведёте несколько аккаунтов без хаоса и ежедневной рутины.</p>
              </div>
            </div>
            <div className="feature-image">
              <div className="window-wrapper">
                <div className="window-header">
                  <div className="window-controls">
                    <div className="window-control close"></div>
                    <div className="window-control minimize"></div>
                    <div className="window-control maximize"></div>
                  </div>
                  <div className="window-title">Управление проектами</div>
                </div>
                <div className="window-content">
                  <div className="image-placeholder"></div>
                </div>
              </div>
            </div>
          </div>

         
        </div>
      </section>

      

      {/* Testimonials */}
      <section className="testimonials" ref={testimonialsContainerRef} style={{ display: 'none' }}>
        <div className="container">
          <h2 className="section-title">Что говорят пользователи</h2>
          
          <div 
            className="testimonials-grid"
            ref={setTestimonialsRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onWheel={handleWheel}
          >
            <div className="testimonial">
              <p>«Контент-план оказался не просто полезным, а прямо в точку — все как я говорю и думаю. Сценарии — супер!»</p>
              <div className="author">— Алена, психолог</div>
            </div>
            
            <div className="testimonial">
              <p>«Рилсы пошли. Удивительно, но Dissai реально понимает, что работает.»</p>
              <div className="author">— Тимур, лайфстайл-блогер</div>
            </div>
            
            <div className="testimonial">
              <p>«Я использую это для клиентов — они в восторге. Никаких больше "что постить завтра".»</p>
              <div className="author">— Света, СММщик</div>
            </div>
            
            <div className="testimonial">
              <p>«Наконец-то контент, который продает! Клиенты сами пишут после моих постов.»</p>
              <div className="author">— Максим, предприниматель</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing" ref={pricingRef}>
        <div className="container">
          <h2 className="section-title">Выберите план, который решает ваши задачи</h2>

          {/* Переключатели периодов оплаты - показываем только если есть больше одного периода */}
          {availablePeriods.length > 1 && (
            <div className="flex justify-center mb-8 overflow-x-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-1 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-nowrap min-w-max">
              {availablePeriods.map((period) => {
                const periodDiscount = getDiscountForPeriod(period);
                const hasDiscount = periodDiscount && periodDiscount.discountPercent > 0;

                return (
                  <button
                    key={period}
                    onClick={() => setSelectedPeriod(period)}
                    className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                      selectedPeriod === period
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                        : 'text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {period} {getPlural(period, 'месяц', 'месяца', 'месяцев')}
                    {hasDiscount && (
                      <span className={`ml-1.5 md:ml-2 px-1.5 py-0.5 md:px-2 md:py-0.5 rounded-full text-xs font-medium ${
                        selectedPeriod === period
                          ? 'bg-white/20 text-white'
                          : 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200'
                      }`}>
                        -{periodDiscount.discountPercent}%
                      </span>
                    )}
                  </button>
                );
              })}
              </div>
            </div>
          )}

          <div className="pricing-grid" style={{gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: '1000px'}}>
            <div className="pricing-card">
              <h3>СТАРТ</h3>
              <p className="plan-description">Идеально: для экспертов и начинающих блогеров</p>
              <div className="price">
                {getMonthlyPrice('Individual', selectedPeriod, 'Старт').toLocaleString('ru-RU')} ₽
              </div>
              <div className="price-note mt-0 mb-2">в месяц</div>
              <div className={`pricing-total-price ${selectedPeriod === 1 ? 'empty' : ''}`}>
                {selectedPeriod === 1 ? '' : `Итого: ${calculatePriceWithDiscount('Individual', selectedPeriod, 'Старт').toLocaleString('ru-RU')} ₽`}
              </div>
              <button className="btn-primary py-2" onClick={() => handlePlanSelect('expert')}>Начать бесплатно</button>
              <div className="btn-note-standalone">3 дня бесплатно</div>
              <div className="features-section">
                <h4>Это включает:</h4>
              </div>
              <ul className="features-list">
                <li><Check size={16} /> 1 анализ целевой аудитории</li>
                <li><Check size={16} /> 1 контент план</li>
                <li><Check size={16} /> 500 анализов рилс</li>
              </ul>
            </div>
            
            <div className="pricing-card popular">
              <div className="popular-badge">Популярный</div>
              <h3>ПРОФИ</h3>
              <p className="plan-description">Идеально: для профессиональных блогеров и SMM-специалистов</p>
              <div className="price">
                {getMonthlyPrice('Individual', selectedPeriod, 'Профи').toLocaleString('ru-RU')} ₽
              </div>
              <div className="price-note mt-0">в месяц</div>
              <div className={`pricing-total-price ${selectedPeriod === 1 ? 'empty' : ''}`}>
                {selectedPeriod === 1 ? '' : `Итого: ${calculatePriceWithDiscount('Individual', selectedPeriod, 'Профи').toLocaleString('ru-RU')} ₽`}
              </div>
              <button className="btn-primary py-2" onClick={() => handlePlanSelect('pro')}>Начать бесплатно</button>
              <div className="btn-note-standalone">3 дня бесплатно</div>
              <div className="features-section">
                <h4>Это включает:</h4>
              </div>
              <ul className="features-list">
                <li><Check size={16} /> 6 анализов целевой аудитории</li>
                <li><Check size={16} /> 6 контент планов</li>
                <li><Check size={16} /> 1500 анализов рилс</li>
              </ul>
            </div>
            
            <div className="pricing-card">
              <h3>БИЗНЕС</h3>
              <p className="plan-description">Идеально: для команд и агентств</p>
              <div className="price">
                {getMonthlyPrice('Team', selectedPeriod, 'Бизнес').toLocaleString('ru-RU')} ₽
              </div>
              <div className="price-note mt-0">в месяц</div>
              <div className={`pricing-total-price ${selectedPeriod === 1 ? 'empty' : ''}`}>
                {selectedPeriod === 1 ? '' : `Итого: ${calculatePriceWithDiscount('Team', selectedPeriod, 'Бизнес').toLocaleString('ru-RU')} ₽`}
              </div>
              <button className="btn-primary py-2" onClick={() => handlePlanSelect('team')}>Выбрать</button>
              <div className="features-section">
                <h4>Это включает:</h4>
              </div>
              <ul className="features-list">
                <li><Check size={16} /> Всё из Pro</li>
                <li><Check size={16} /> 5 участников</li>
                <li><Check size={16} /> Доп. участник +3000 ₽</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/*
      Referral Program (скрыто)
      <section className="referral-program" ref={referralRef}>
        <div className="container">
          <h2 className="section-title">Приводите клиентов и зарабатывайте вместе с нами</h2>
          <div className="referral-grid">
            <div className="referral-card">
              <div className="referral-header">
                <div className="referral-icon">
                  <Users size={32} />
                </div>
                <h3>Личная программа</h3>
                <p className="referral-subtitle">для всех</p>
              </div>
              <div className="referral-benefits">
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <CreditCard size={20} />
                  </div>
                  <div className="benefit-content">
                    <h4>Для вас</h4>
                    <p>15% от первого платежа приглашенного друга</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <TrendingDown size={20} />
                  </div>
                  <div className="benefit-content">
                    <h4>Для друга</h4>
                    <p>Скидка 5% на первый платёж</p>
                  </div>
                </div>
              </div>
              <div className="referral-note">
                <p>Заработанные средства расходуются на оплату подписки</p>
              </div>
            </div>
            <div className="referral-card featured">
              <div className="referral-header">
                <div className="referral-icon">
                  <TrendingUp size={32} />
                </div>
                <h3>Профессиональная программа</h3>
                <p className="referral-subtitle">для блогеров и ИП</p>
              </div>
              <div className="referral-benefits">
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <CreditCard size={20} />
                  </div>
                  <div className="benefit-content">
                    <h4>Для вас</h4>
                    <p>50% от первого платежа приглашенного друга</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <TrendingDown size={20} />
                  </div>
                  <div className="benefit-content">
                    <h4>Для друга</h4>
                    <p>Скидка 15% на первый платёж</p>
                  </div>
                </div>
              </div>
              <div className="referral-note">
                <p>Заработанные средства можно вывести на расчетный счёт по договору</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* FAQ */}
      <section className="faq" ref={faqRef}>
        <div className="container">
          
          <div className="faq-list">
            <div className="faq-item">
              <button 
                className={`faq-question ${openFaq === 0 ? 'active' : ''}`}
                onClick={() => toggleFaq(0)}
              >
                <span>Чем Dissai лучше обычного ChatGPT?</span>
                <ChevronDown size={20} />
              </button>
              {openFaq === 0 && (
                <div className="faq-answer">
                  ChatGPT — это общий инструмент для широких задач. Dissai — ваш персональный стратег для Instagram. Мы анализируем вашу аудиторию, конкурентов и тренды, чтобы создавать контент, который действительно работает. Вы получаете не просто текст, а готовую стратегию роста.
                </div>
              )}
            </div>
            
            <div className="faq-item">
              <button 
                className={`faq-question ${openFaq === 1 ? 'active' : ''}`}
                onClick={() => toggleFaq(1)}
              >
                <span>Не будет ли контент шаблонным?</span>
                <ChevronDown size={20} />
              </button>
              {openFaq === 1 && (
                <div className="faq-answer">
                  Абсолютно нет! ИИ изучает именно ваш аккаунт: стиль общения, историю публикаций и особенности ниши. Каждая идея адаптируется под вашу уникальность — вы получите персонализированный контент, а не шаблонные предложения.

                </div>
              )}
            </div>
            
            <div className="faq-item">
              <button 
                className={`faq-question ${openFaq === 2 ? 'active' : ''}`}
                onClick={() => toggleFaq(2)}
              >
                <span>Какую выгоду я получу?</span>
                <ChevronDown size={20} />
              </button>
              {openFaq === 2 && (
                <div className="faq-answer">
                  Вы сэкономите 10-15 часов в неделю на создание контента и получите готовую стратегию для роста охватов и вовлеченности. При этом начать можно бесплатно — вы ничего не теряете, но многое приобретаете.

                </div>
              )}
            </div>

            <div className="faq-item">
              <button 
                className={`faq-question ${openFaq === 3 ? 'active' : ''}`}
                onClick={() => toggleFaq(3)}
              >
                <span>Подходит ли для любой ниши?</span>
                <ChevronDown size={20} />
              </button>
              {openFaq === 3 && (
                <div className="faq-answer">
                  Да, Dissai работает с любыми нишами — от бизнеса и психологии до лайфстайла и творчества. AI адаптируется под вашу специфику.
                </div>
              )}
            </div>

            <div className="faq-item">
              <button 
                className={`faq-question ${openFaq === 4 ? 'active' : ''}`}
                onClick={() => toggleFaq(4)}
              >
                <span>Когда я увижу первые результаты?</span>
                <ChevronDown size={20} />
              </button>
              {openFaq === 4 && (
                <div className="faq-answer">
                  Первый готовый контент-план вы получите через 15 минут после регистрации. Рост охватов и вовлеченности заметите через 2-3 недели регулярных публикаций, но в большей степени все зависит от вас.
                </div>
              )}
            </div>
            
            <div className="faq-item">
              <button 
                className={`faq-question ${openFaq === 5 ? 'active' : ''}`}
                onClick={() => toggleFaq(5)}
              >
                <span>Нужен ли VPN для работы?</span>
                <ChevronDown size={20} />
              </button>
              {openFaq === 5 && (
                <div className="faq-answer">
                  Нет! Наш сервис полностью адаптирован для работы в России и СНГ. Все функции доступны без VPN — просто заходите и начинайте работать.

                </div>
              )}
            </div>

            <div className="faq-item">
              <button 
                className={`faq-question ${openFaq === 6 ? 'active' : ''}`}
                onClick={() => toggleFaq(6)}
              >
                <span>Какие способы оплаты принимаете?</span>
                <ChevronDown size={20} />
              </button>
              {openFaq === 6 && (
                <div className="faq-answer">
                  Мы принимаем карты российских банков (Мир, VISA, Mastercard). Оплата в рублях — без скрытых комиссий и переплат.
                </div>
              )}
            </div>
            
            <div className="faq-item">
              <button 
                className={`faq-question ${openFaq === 7 ? 'active' : ''}`}
                onClick={() => toggleFaq(7)}
              >
                <span>Можно ли отменить подписку?</span>
                <ChevronDown size={20} />
              </button>
              {openFaq === 7 && (
                <div className="faq-answer">
                  Да, в любой момент. Никаких обязательств — отмените подписку одним кликом в настройках аккаунта и при этом сохраните оставшиеся дни.
                </div>
              )}
            </div>
            
            
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" ref={ctaRef}>
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2 className="cta-title">Контент-план и вирусные идеи для Reels на месяц <br/> за 15 минут</h2>
              <p className="cta-subtitle">
                Начни прямо сейчас и получи свой персональный контент-план уже через 15 минут
              </p>
            </div>
            <div className="cta-action">
              <button className="btn-cta-primary" onClick={handleGetStarted}>
                Получить контент-план
                <ArrowRight size={20} />
              </button>
              <p className="cta-note">Без карты • 3 дня бесплатно • Отмена в любой момент</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Dissai</h3>
              <p>Персональный контент-план для вашей страницы</p>
            </div>
            
            <div className="footer-section">
              <h4>Контакты</h4>
              <p>support@dissai.ru</p>
              <p>ООО "ДИСАЙ"</p>
              <p>367000, Респ. Дагестан, г. Махачкала, ул. Абубакарова, д. 115, каб. 2 помещ. 2</p>
              <p>ИНН: 0500031568</p>
              <p>ОГРН: 1250500006283</p>
            </div>
            
            <div className="footer-section">
              <h4>Юридическая информация</h4>
              <a href="/privacy-policy" className="footer-link">Политика конфиденциальности и обработки персональных данных</a>
              <a href="/public-offer" className="footer-link">Публичная оферта</a>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 Dissai. Все права защищены.</p>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
}

export default LandingPage;